<template>
  <div class="video-news news-page-container">
    <div class="news-container">
      <h2>视频专区</h2>
      <!-- 标题下分割线 -->
      <div class="title-divider"></div>

      <div v-if="loading" class="loading-text">加载中...</div>
      <div v-else-if="newsList.length > 0" class="video-grid">
        <div
          v-for="item in newsList"
          :key="item.id"
          class="video-card"
          @click="goToDetail(item)"
        >
          <!-- 封面图片区域 -->
          <div class="video-cover">
            <img
              v-if="item.thumbnail"
              :src="item.thumbnail"
              :alt="item.articleTitle"
            />
            <div v-else class="no-cover">
              <span>暂无封面</span>
            </div>
            <!-- 悬停蒙版和播放按钮 -->
            <div class="video-mask">
              <div class="play-button">
                <i class="el-icon-video-play"></i>
              </div>
            </div>
          </div>

          <!-- 标题信息区域 -->
          <div class="video-info">
            <h3 class="video-title">{{ item.articleTitle || '暂无标题' }}</h3>
            <p class="video-subhead">{{ item.subhead || '' }}</p>
          </div>
        </div>
      </div>
      <div v-else class="no-data">暂无数据</div>

      <!-- 分页组件 -->
      <div v-if="total > 0" class="pagination-wrapper">
        <el-pagination
          background
          layout="prev, pager, next"
          :total="total"
          :current-page.sync="currentPage"
          :page-size="pageSize"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- 视频播放弹窗 -->
    <el-dialog
      title="视频播放"
      :visible.sync="videoDialogVisible"
      width="70%"
      :before-close="closeVideoDialog"
      class="video-dialog"
      center
    >
      <div class="video-wrapper">
        <video
          v-if="currentVideoUrl"
          :src="currentVideoUrl"
          controls
          autoplay
          class="video-player"
        >
          您的浏览器不支持视频播放
        </video>
        <div v-else class="no-video">
          <p>暂无视频内容</p>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
  import { getNewsListApi } from '@/API/news.js'
  import { getCategoryIdByName } from '@/utils/getCategoryId.js'
  import { getVideoDetail } from '@/utils/newsNavigation.js'

  export default {
    data() {
      return {
        newsList: [],
        loading: false,
        categoryName: '视频专区', // 当前页面对应的分类名称
        currentPage: 1,
        pageSize: 8, // 修改为8条，支持4列2行布局
        total: 0,
        // 视频播放相关
        videoDialogVisible: false,
        currentVideoUrl: ''
      }
    },
    mounted() {
      this.getNewsList()
    },
    methods: {
      // 获取新闻列表
      async getNewsList() {
        this.loading = true
        try {
          // 1. 先通过分类名称获取对应的分类标识
          const categoryIdentification = await getCategoryIdByName(this.categoryName)

          if (!categoryIdentification) {
            console.error('未找到对应的分类标识')
            this.newsList = []
            this.total = 0
            return
          }

          // 2. 使用获取到的 categoryIdentification 查询新闻列表
          const res = await getNewsListApi(categoryIdentification, this.currentPage, this.pageSize)

          // 根据实际返回的数据结构进行处理
          if (res && res.data) {
            this.newsList = res.data.list || res.data.records || res.data || []
            this.total = res.data.total || res.data.totalCount || 0
          }
        } catch (error) {
          console.error('获取新闻列表失败：', error)
        } finally {
          this.loading = false
        }
      },

      // 分页切换
      handlePageChange(page) {
        this.currentPage = page
        this.getNewsList()
      },

      // 点击视频项 - 获取视频详情并播放
      async goToDetail(newsItem) {
        if (!newsItem || !newsItem.articleId) {
          console.error('视频项数据无效:', newsItem)
          this.$message.error('视频数据无效')
          return
        }

        try {
          // 调用公共方法获取视频详情
          const result = await getVideoDetail(newsItem.articleId)

          if (result.success && result.data && result.data.content) {
            // 获取视频地址
            this.currentVideoUrl = result.data.content
            this.videoDialogVisible = true
          } else {
            console.error('未找到视频地址:', result)
            this.$message.error(result.message || '未找到视频地址')
          }
        } catch (error) {
          console.error('获取视频详情失败:', error)
          this.$message.error('获取视频失败，请稍后重试')
        }
      },

      // 关闭视频弹窗
      closeVideoDialog() {
        this.videoDialogVisible = false
        this.currentVideoUrl = ''
      }
    }
  }
</script>

<style scoped lang="scss">
  // 新闻页面容器样式
  .news-page-container {
    width: 100%;
    padding: 20px 0;
  }

  .news-container {
    width: 94%;
    max-width: 1280px;
    margin: 0 auto;
    padding: 40px 20px;

    // 页面标题样式
    h2 {
      font-size: 28px;
      color: #0555b6; // 蓝色标题
      margin-bottom: 20px;
      text-align: center;
      font-weight: bold;
    }

    // 标题下方分割线
    .title-divider {
      width: 100%;
      height: 1px;
      background-color: #0555b6; // 与标题颜色一致的蓝色
      margin-bottom: 40px;
    }

    // 加载和无数据提示
    .loading-text,
    .no-data {
      text-align: center;
      font-size: 16px;
      color: #999;
      padding: 60px 0;
    }

    // 视频网格布局 - 4列
    .video-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 30px 20px;
      margin-bottom: 20px;
    }

    // 视频卡片样式
    .video-card {
      background: #fff;
      border-radius: 8px;
      overflow: hidden;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);

        .video-cover .video-mask {
          opacity: 1;
        }

        .video-info .video-title {
          color: #0555b6;
        }
      }

      // 封面图片区域
      .video-cover {
        position: relative;
        width: 100%;
        padding-bottom: 66.67%; // 3:2 宽高比
        overflow: hidden;
        background-color: #f5f5f5;

        img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        // 无封面提示
        .no-cover {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #f0f0f0;
          color: #999;
          font-size: 14px;
        }

        // 悬停蒙版
        .video-mask {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;

          // 播放按钮
          .play-button {
            width: 60px;
            height: 60px;
            transition: all 0.3s ease;

            i {
              font-size: 60px;
              color: #fff;
              margin-left: 4px; // 微调播放图标位置使其居中
            }

            &:hover {
              transform: scale(1.1);
            }
          }
        }
      }

      // 标题信息区域
      .video-info {
        padding: 15px;

        .video-title {
          font-size: 16px;
          font-weight: bold;
          color: #333;
          line-height: 1.4;
          margin-bottom: 8px;
          transition: color 0.3s ease;

          // 最多显示2行，超出省略
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
          overflow: hidden;
          text-overflow: ellipsis;
          word-break: break-word;
          min-height: 44px; // 确保即使只有一行也保持高度一致
        }

        .video-subhead {
          font-size: 13px;
          color: #666;
          line-height: 1.5;

          // 最多显示2行，超出省略
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
          overflow: hidden;
          text-overflow: ellipsis;
          word-break: break-word;
          min-height: 39px; // 保持高度一致
        }
      }
    }

    // 分页组件样式
    .pagination-wrapper {
      display: flex;
      justify-content: center;
      margin-top: 40px;
      padding: 20px 0;
    }
  }

  // 视频弹窗样式
  .video-dialog {
    ::v-deep .el-dialog__body {
      padding: 20px;
      background-color: #000;
    }

    .video-wrapper {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 400px;

      .video-player {
        width: 100%;
        max-height: 70vh;
        outline: none;
      }

      .no-video {
        color: #fff;
        font-size: 16px;
        text-align: center;

        p {
          margin: 0;
        }
      }
    }
  }

  // 响应式设计
  @media (max-width: 1200px) {
    .news-container .video-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  @media (max-width: 768px) {
    .news-container .video-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 20px 15px;
    }

    .news-container h2 {
      font-size: 24px;
    }
  }

  @media (max-width: 480px) {
    .news-container .video-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
